import extend from 'extend';
import mapItem from '../../lib/mapItem';
import mapTempItem from '../../lib/mapTempItem';
import currentItemInfo from '../../lib/currentItemInfo';
import { getProductAliasFromType } from '../../lib/productHelpers';
import { fetchInventoryInfo, fetchOrderItemOptions } from '../../actions/exchange';
import { noItemImage } from '../../constants/defaultImages';
import { toggleMaxItemsReached } from '../../actions/restrictions';
import getPrevSelectedItemCount from '../../lib/getPrevSelectedItemCount';
import {
  getNestedReasons,
  getRetailerReasonCode,
  parseReasonJson,
  partitionReasonsByProduct,
} from '../../lib/reasons';
import {
  saveNestedDisplayedReturnReasons,
  saveSelectedNestedReturnReason,
  saveSelectedReturnReason,
} from '../../actions/returnReasonsMonitoring';
import { deleteCachedImages, resetPhotoUploadState } from '../../actions/photoUpload';
import { returnItemsService } from '../../services';
import { store } from '../../store';
import { RECEIVE_CLAIMS_SIDEBAR_TOGGLE } from '../../actions/claims';
import { ActionTypes } from '../../constants/enums';
import { refreshOrderEligibilities } from '../../actions/orderService';
import { setIsFetchingAddCartItem, setIsFetchingRemoveCartItem } from '../../actions/itemsPage';
import {
  receiveUnevenExchangeData,
  resetUnevenExchangesState,
} from '../../actions/unevenExchanges';
import { formatCurrencyValue, getCurrencyExponent } from '../../lib/currencyFormatters';

/* eslint-disable no-use-before-define */

const exchangeSidebarComponent = () => {
  const {
    claims: { enabled: claimsEnabled } = {},
    page: { productType },
    returnsSettings: {
      [productType]: {
        exchange: { unevenExchanges, hidden_options: hiddenExchangeOptions } = {},
        ineligible_reasons: ineligibleReasons = {},
      } = {},
    },
  } = store.getState();
  const node = document.createElement('div');
  document.body.appendChild(node);
  let updateSelectionCallback = null;
  let updateToggleReturnsCallback = null;
  let sidebarState = {
    open: false,
    currentItem: null,
    selectedExchangeItem: {},
    exchangeComment: '',
    savedItems: {},
    itemQuantity: null,
    itemQuantityList: null,
    itemFinalized: false,
    returnQuantity: 0,
    temporaryItems: {},
    selectedProduct: null,
    inventory: [],
    options: [],
    productQtyOptions: {},
    itemQtyOptions: {},
    outOfStockError: false,
    inventoryError: false,
    exchangeSelectionRequired: true,
    currentInventory: [],
    selectedOptions: {},
    exchangeOptions: {},
    commentHidden: null,
    commentRequired: null,
    commentPlaceholder: null,
    returnEligibility: true,
    inventoryUnavailable: false,
    loading: false,
    returnOptionsItems: {},
    exchangeReasons: null,
    selectedReason: null,
    selectedNestedReason: null,
    nestedReasons: null,
    photoUploadEnabled: false,
    photoUploadRequired: false,
    photoUploadPlaceholder: '',
    photoUploadTitle: '',
    photoUploadMaxFiles: undefined,
  };

  const updateState = (newState, deep = true) => {
    sidebarState = extend(deep, sidebarState, newState);
    renderSidebar();
  };

  const actions = {
    initialize: (item, selectedProduct) =>
      item.validate_return_options
        ? fetchReturnItemOptions(item, selectedProduct)
        : checkExchangeInventory(item, selectedProduct),
    toggle: (event, cancelExchange) => updateState(toggleSidebar(cancelExchange)),
    resetSelectedProduct: (itemId) => updateState(resetSelectedProduct(itemId)),
    handleSelectProduct: (index, product) => updateState(handleSelectProduct(product)),
    resetProductSelection: () => {
      resetVariantSelection();
      updateState({
        selectedProduct: null,
        selectedReason: null,
        selectedNestedReason: null,
        nestedReasons: null,
      });
      if (claimsEnabled) {
        store.dispatch({ type: RECEIVE_CLAIMS_SIDEBAR_TOGGLE, data: { sidebarOpen: true } });
        actions.resetSidebar();
      }
    },
    saveItem: async () => {
      const updates = await handleItemSelection();
      updateState(updates);
      actions.toggle();
    },
    deleteItem: async () => {
      const updates = await cancelItem();
      updateState(updates);
    },

    saveItemProgress: () =>
      updateState({
        temporaryItems: {
          [sidebarState.currentItem.item_id]: currentItemInfo(sidebarState),
        },
      }),
    resetSelection: () => updateState(resetSelection()),
    resetSidebar: () => updateState({ open: false, temporaryItems: {}, savedItems: {} }, false),
    resetItemInfo: () => updateState(currentItemInfo({})),
    setInventoryErrorState: (val) => updateState({ inventoryError: val }),
    updateInventoryOptions: (inventory, options) => {
      if (unevenExchanges) {
        store.dispatch(receiveUnevenExchangeData({ inventory, options }));
      }
      return updateState({
        inventory,
        options,
        inventoryUnavailable: inventory.length === 0,
        exchangeOptions: initializeExchangeOptions(inventory, options),
        productQtyOptions: Array(sidebarState.itemQuantity)
          .fill(0)
          .map((qty, index) => index + 1)
          .reduce((acc, qty) => {
            acc[qty] = true;
            return acc;
          }, {}),
      });
    },
    handleQtySelect: (event) => updateState({ returnQuantity: parseInt(event.value, 10) }),
    setLoadingState: (loading) => updateState({ loading }),
    handleCommentInput: (commentData) => updateState(updateExchangeComment(commentData)),
  };

  const setUpdateCallback = (toggleReturnsCallback, selectionCallback) => {
    updateToggleReturnsCallback = toggleReturnsCallback;
    updateSelectionCallback = selectionCallback;
  };

  const resetSelectedProduct = (itemId) => {
    const { temporaryItems } = sidebarState;
    if (temporaryItems[itemId]) {
      temporaryItems[itemId].selectedProduct = null;
    }

    return {
      temporaryItems,
    };
  };

  const getReasonInfo = () => {
    const { selectedReason, selectedNestedReason, exchangeComment, commentHidden } = sidebarState;
    const reasonInfo = {};

    if (selectedNestedReason) {
      reasonInfo.return_reason_code_id = selectedNestedReason.return_reason_code_id;
      reasonInfo.return_desc = selectedNestedReason.display_name;
    } else if (selectedReason) {
      reasonInfo.return_reason_code_id = selectedReason.return_reasons[0].return_reason_code_id;
      reasonInfo.return_desc = selectedReason.reason_category;
    }

    reasonInfo.user_comment = commentHidden ? '' : exchangeComment;

    return reasonInfo;
  };

  const handleItemSelection = async () => {
    const {
      restrictions: { itemLimit },
      photoUpload: { images },
    } = store.getState();
    const {
      currentInventory,
      currentItem,
      selectedOptions,
      savedItems,
      returnQuantity,
      exchangeComment,
      selectedReason,
      selectedNestedReason,
    } = sidebarState;
    const selectedItem = savedItems[currentItem.item_id] || currentItem;
    const selectedItemId = selectedItem.item_id;
    const selectUpdate = { ...sidebarState.saveditems, itemFinalized: true };
    returnItemsService.removeItemById(selectedItem);
    const retailerCode = getRetailerReasonCode({ selectedReason, selectedNestedReason });

    if (ineligibleReasons && ineligibleReasons[retailerCode]) {
      selectedItem.selected = false;
      selectedItem.ineligibilityInfo = ineligibleReasons[retailerCode];
    } else {
      selectedItem.selected = true;
      selectedItem.return_qty = returnQuantity;
      selectedItem.selectedOptions = selectedOptions;
      selectedItem.product = getProductAliasFromType('exchange');
      selectedItem.exchange_item = currentInventory[0]; // eslint-disable-line
      selectedItem.total_price = 0;
      selectedItem.user_comment = exchangeComment;
      extend(selectedItem, getReasonInfo());
      returnItemsService.addItem(selectedItem);
      extend(selectUpdate, {
        savedItems: { [selectedItemId]: selectedItem },
      });
    }

    if (images.length) {
      selectedItem.uploadedImages = images;
      selectedItem.item_images = images.map(({ filename }) => filename);
      store.dispatch(resetPhotoUploadState());
    }

    const prevSelectedItemCount = getPrevSelectedItemCount(savedItems, selectedItemId);
    const maxItemsReached = prevSelectedItemCount + 1 === itemLimit;
    if (maxItemsReached) {
      store.dispatch(toggleMaxItemsReached(maxItemsReached));
    }

    if (claimsEnabled) {
      selectedItem.action_type = ActionTypes.Exchange;
      if (selectedItem.return_reason_code_id) {
        selectedItem.action_reason_id = `${selectedItem.return_reason_code_id}`;
      }
      delete selectedItem.return_reason_code_id;
      try {
        store.dispatch(setIsFetchingAddCartItem(true));
        await store.dispatch(refreshOrderEligibilities(returnItemsService.getItems()));
      } catch (err) {
        console.log(err);
      } finally {
        store.dispatch(setIsFetchingAddCartItem(false));
        returnItemsService.updateCartActions();
      }
    }

    updateSelectionCallback(selectedItem, !maxItemsReached, false);

    return selectUpdate;
  };

  const handleUnevenExchangeSubmit = (data) => {
    const {
      page: { locale },
      restrictions: { itemLimit },
      photoUpload: { images },
      unevenExchanges: { exchangeVariants },
    } = store.getState();
    const { variantId } = data.value.exchangeTo;
    const exchangeTo = exchangeVariants.find((variant) => variant.variantId === variantId);
    const exchangeToPrice = exchangeTo.price.value / getCurrencyExponent('USD');
    const { currentItem, savedItems, returnQuantity } = sidebarState;
    const selectedItem = savedItems[currentItem.item_id] || currentItem;
    const selectedItemId = selectedItem.item_id;
    const selectUpdate = { ...sidebarState.saveditems, itemFinalized: true };
    const attributes = Object.values(exchangeTo.variantInfo).reduce(
      (acc, option) => {
        if (option.optionId === 'product') return acc;
        return {
          ...acc,
          [option.optionId]: option.value,
        };
      },
      {
        quantity: returnQuantity,
        price: formatCurrencyValue({ value: exchangeToPrice, locale }),
      },
    );
    returnItemsService.removeItemById(selectedItem);
    selectedItem.selected = true;
    selectedItem.return_qty = returnQuantity;
    selectedItem.product = getProductAliasFromType('exchange');
    selectedItem.exchange_item = {
      product_id: exchangeTo.productId,
      variant_id: exchangeTo.variantId,
      ext_id: exchangeTo.variantId,
      title: exchangeTo.name,
      sku: exchangeTo.sku,
      item_image: exchangeTo.imageUrl,
      price: exchangeToPrice,
      currency: exchangeTo.price.currency || 'USD',
      quantity: returnQuantity,
      attributes,
    };
    selectedItem.selectedOptions = attributes;
    extend(selectedItem, getReasonInfo());
    returnItemsService.addItem(selectedItem);
    extend(selectUpdate, {
      savedItems: { [selectedItemId]: selectedItem },
    });
    if (images.length) {
      selectedItem.uploadedImages = images;
      selectedItem.item_images = images.map(({ filename }) => filename);
      store.dispatch(resetPhotoUploadState());
    }
    const prevSelectedItemCount = getPrevSelectedItemCount(savedItems, selectedItemId);
    const maxItemsReached = prevSelectedItemCount + 1 === itemLimit;
    if (maxItemsReached) {
      store.dispatch(toggleMaxItemsReached(maxItemsReached));
    }
    updateSelectionCallback(selectedItem, !maxItemsReached, false);
    updateState(selectUpdate);
    store.dispatch(resetUnevenExchangesState());
    actions.toggle();
  };

  const itemInit = (item, productSelection = 'INITIAL') => {
    const { temporaryItems } = sidebarState;
    const { itemId, itemName, itemProperties, itemQuantity, returnReasonCodes, returnEligibility } =
      mapItem(item);

    const tempItem = temporaryItems[itemId];
    const { exchangeReasons } = partitionReasonsByProduct(returnReasonCodes);
    const isExchangeReasonPlaceholderOnly =
      exchangeReasons.length === 1 &&
      exchangeReasons[0].return_reasons &&
      exchangeReasons[0].return_reasons.length === 1;

    let selectedProduct = null;
    if (productSelection === 'INITIAL' && tempItem) {
      // eslint-disable-next-line prefer-destructuring
      selectedProduct = tempItem.selectedProduct;
    }
    if (productSelection === 'exchange') {
      selectedProduct = 'exchange';
    }

    const itemUpdate = {
      currentItem: { ...(tempItem || item), itemName, itemProperties },
      itemProperties,
      temporaryItems,
      itemQuantity,
      exchangeReasons:
        exchangeReasons.length > 0 && !isExchangeReasonPlaceholderOnly ? exchangeReasons : null,
      // If item is eligible for exchange ONLY, do not show product selection step
      selectedProduct: returnEligibility ? selectedProduct : getProductAliasFromType('exchange'),
      returnEligibility,
      itemFinalized: false,
    };

    if (!tempItem) {
      itemUpdate.temporaryItems[itemId] = { ...item };
      initializeSidebarByProduct(itemUpdate.selectedProduct, itemId);
    } else if (selectedProduct === getProductAliasFromType('return')) {
      // close
      actions.toggle();
      updateToggleReturnsCallback(tempItem);
      return false;
    } else {
      tempItem.selectedProduct = selectedProduct;
      extend(itemUpdate, mapTempItem(tempItem));
    }

    return itemUpdate;
  };

  const checkExchangeInventory = async (item, selectedProduct) => {
    const { returnEligibility } = mapItem(item);
    actions.setLoadingState(true);
    updateState(itemInit(item, selectedProduct), false);

    const handleNoInventory = () => {
      if (returnEligibility) {
        actions.toggle();
        return updateToggleReturnsCallback({ ...item, inventoryNotAvailable: true }, true);
      }
      return actions.updateInventoryOptions([], []);
    };

    try {
      const itemId = item.item_id;
      const {
        inventory,
        options,
        error_code: errorCode,
      } = await store.dispatch(fetchInventoryInfo(itemId));

      if (inventory && options && inventory.length && options.length && !errorCode) {
        return actions.updateInventoryOptions(inventory, filterOptions(options));
      }

      return handleNoInventory();
    } catch (error) {
      return handleNoInventory();
    } finally {
      actions.setLoadingState(false);
    }
  };

  const fetchReturnItemOptions = async (item, selectedProduct) => {
    actions.setLoadingState(true);
    const itemId = item.item_id;
    const returnProductType = getProductAliasFromType('return');
    const exchangeIneligibility = (options) =>
      options && options.length === 1 && options.includes(returnProductType);
    const handleReturnProduct = (options) => {
      if (exchangeIneligibility(options)) {
        handleSelectProduct({ type: returnProductType });
      }
    };
    const {
      returnOptionsItems,
      returnOptionsItems: { [itemId]: cachedReturnOptions },
    } = sidebarState;
    updateState(
      itemInit(item, exchangeIneligibility(cachedReturnOptions) ? undefined : selectedProduct),
      false,
    );
    if (cachedReturnOptions) {
      actions.setLoadingState(false);
      handleReturnProduct(cachedReturnOptions);
      return;
    }
    try {
      const { return_options: returnOptions } = await store.dispatch(fetchOrderItemOptions(itemId));
      handleReturnProduct(returnOptions);
      updateState({ returnOptionsItems: { ...returnOptionsItems, [itemId]: returnOptions } });
      // eslint-disable-next-line no-empty
    } catch (e) {
    } finally {
      actions.setLoadingState(false);
    }
  };

  const resetSelection = () => ({
    options: [],
    inventory: [],
    currentInventory: [],
    selectedOptions: {},
    exchangeOptions: {},
    productQtyOptions: {},
    itemQtyOptions: {},
  });

  const toggleSidebar = (cancelExchange = false) => {
    const {
      restrictions: { maxItemsReached },
      itemsPage: { isFetchingAddCartItem, isFetchingRemoveCartItem },
    } = store.getState();
    const isFetching = isFetchingAddCartItem || isFetchingRemoveCartItem;

    if (isFetching) {
      return { open: true };
    }

    if (sidebarState.open && maxItemsReached && !sidebarState.itemFinalized) {
      return { open: true };
    }

    if (!sidebarState.currentItem) {
      return { open: false };
    }

    if (sidebarState.open) {
      if (!cancelExchange) {
        actions.saveItemProgress();
      }
      actions.resetItemInfo();
      actions.resetSelection();
      store.dispatch(resetPhotoUploadState());
    }

    return { open: !sidebarState.open };
  };

  const checkSelected = (product) => {
    const { selectedProduct } = sidebarState;
    return product.type === selectedProduct;
  };

  const cancelItem = async (overrideToggle) => {
    const {
      restrictions: { itemLimit },
      claims: { enabled: claimsEnabled },
      itemsPage: { isFetchingRemoveCartItem },
    } = store.getState();
    if (isFetchingRemoveCartItem) return {};

    const { currentItem, savedItems, temporaryItems, selectedExchangeItem } = sidebarState;
    currentItem.selected = false;
    currentItem.selectedProduct = null;
    currentItem.options = [];
    currentItem.inventory = [];
    currentItem.currentInventory = [];
    currentItem.exchangeOptions = {};
    currentItem.selectedOptions = {};
    currentItem.productQtyOptions = {};
    currentItem.itemQtyOptions = {};
    currentItem.product = '';
    currentItem.exchange_item = null;
    returnItemsService.removeItemById(currentItem);

    if (claimsEnabled) {
      try {
        store.dispatch(setIsFetchingRemoveCartItem(true));
        await store.dispatch(refreshOrderEligibilities(returnItemsService.getItems()));
      } catch (err) {
        console.log(err);
      } finally {
        store.dispatch(setIsFetchingRemoveCartItem(false));
      }
    }

    delete savedItems[currentItem.item_id];
    delete temporaryItems[currentItem.item_id];
    selectedExchangeItem[currentItem.item_id] = null;

    const newItemCount = Object.keys(savedItems).length;
    const maxItemsReached = newItemCount === itemLimit;
    store.dispatch(toggleMaxItemsReached(maxItemsReached));

    updateSelectionCallback(currentItem, true, true);

    if (!overrideToggle) {
      // close
      actions.toggle(null, true);
    }

    return { savedItems, temporaryItems, selectedExchangeItem };
  };

  const updateExchangeComment = (comment) => {
    if (!comment.target.value) {
      sidebarState.exchangeComment = '';
    }

    return { exchangeComment: comment.target.value };
  };

  const handleSelectProduct = (product) => {
    updateState({
      selectedProduct: product.type,
    });
    initializeSidebarByProduct(product.type, sidebarState.currentItem.item_id);
  };

  const filterOptions = (options) => {
    if (hiddenExchangeOptions && hiddenExchangeOptions.length) {
      return options.filter((option) => !hiddenExchangeOptions.includes(option.code.toLowerCase()));
    }
    return options;
  };

  const initializeSidebarByProduct = async (product, itemId) => {
    actions.setInventoryErrorState(false);
    if (product === getProductAliasFromType('return')) {
      updateToggleReturnsCallback(sidebarState.currentItem);
      // close
      actions.toggle();
    } else if (product === getProductAliasFromType('exchange')) {
      try {
        const {
          inventory,
          options,
          error_code: errorCode,
        } = await store.dispatch(fetchInventoryInfo(itemId));

        const loadedInventory = inventory || [];
        const loadedOptions = options || [];

        if (loadedInventory.length === 0 || loadedOptions.length === 0 || errorCode) {
          actions.updateInventoryOptions([], filterOptions([]));
        } else {
          actions.updateInventoryOptions(loadedInventory, filterOptions(loadedOptions));
        }
      } catch (error) {
        actions.updateInventoryOptions([], filterOptions([]));
      }
    }
  };

  /*
   * Setup initial values for EXCHANGE item dropdowns based on inventory
   */
  const initializeExchangeOptions = (inventory, options) => {
    const exchangeOptions = {};
    options.forEach((option) => {
      exchangeOptions[option.code] = option.values.reduce(
        (availableOptions, value) => ({
          ...availableOptions,
          [value]: inventory.some((item) => item.attributes[option.code] === value),
        }),
        {},
      );
      exchangeOptions[`${option.code}SortOrder`] = option.values;
    });

    return exchangeOptions;
  };

  /*
   * For every drop-down selection, set that attribute as primary and reset
   * all selected options that are no longer valid.
   */
  const handleOptionChange = (primaryAttr) => (event) => {
    if (event === null) {
      const { selectedOptions } = sidebarState;
      delete selectedOptions[primaryAttr];
      updateState({
        ...sidebarState,
        selectedOptions,
        exchangeSelectionRequired: true,
        returnQuantity: '',
        selectedExchangeItem: { [sidebarState.currentItem.item_id]: sidebarState.currentItem },
      });
      return;
    }

    let itemOutOfStock = false;
    const { inventory, exchangeOptions, itemQuantity, currentItem, options, selectedExchangeItem } =
      sidebarState;
    const currentValue = event.value;
    const selectedOptions = {
      ...sidebarState.selectedOptions,
      [primaryAttr]: currentValue,
    };
    const selectedOptionsKeys = Object.keys(selectedOptions);
    const currentInventory = inventory.filter((item) =>
      selectedOptionsKeys.every((value) => item.attributes[value] === selectedOptions[value]),
    );

    const newSelectedOptions = selectedOptionsKeys.reduce((newSelOptions, attribute) => {
      const attributeValue = selectedOptions[attribute];
      newSelOptions[attribute] = ''; // Reset selection

      // Checking availability with primary attribute
      const itemIsAvailable = inventory.some(
        (item) =>
          item.attributes[primaryAttr] === currentValue &&
          item.attributes[attribute] === attributeValue,
      );

      if (itemIsAvailable) {
        newSelOptions[attribute] = attributeValue;
        exchangeOptions[attribute][attributeValue] = true; // Remove 'SOLD OUT'
      } else if (!itemIsAvailable && attribute === primaryAttr) {
        itemOutOfStock = true;
        newSelOptions[attribute] = currentValue;
      }

      return newSelOptions;
    }, {});
    const noEmptySelection = Object.keys(newSelectedOptions).every(
      (option) => newSelectedOptions[option],
    );

    const optionCodeToKey = (optionCode) =>
      options
        .filter(({ code }) => code === optionCode)
        .map(({ title }) => title)
        .pop();

    let updatedSelectedExchangeItem = {
      ...selectedExchangeItem,
      [currentItem.item_id]: currentItem,
    };
    // All selctions should be set and item_image should be present in order to update item image and name.
    if (
      selectedOptionsKeys.length === options.length &&
      currentInventory &&
      currentInventory.length &&
      'item_image' in currentInventory[0]
    ) {
      const updatedItemProperties = currentItem.item_properties.map((item) =>
        item.key === optionCodeToKey(primaryAttr) ? { ...item, value: currentValue } : item,
      );

      updatedSelectedExchangeItem = {
        ...selectedExchangeItem,
        [currentItem.item_id]: {
          itemName: currentInventory[0].title,
          product_image_url: currentInventory[0].item_image || noItemImage,
          itemProperties: updatedItemProperties,
        },
      };
    }

    const allSelected =
      Object.keys(exchangeOptions).length / 2 === Object.keys(newSelectedOptions).length;
    updateState({
      selectedOptions: newSelectedOptions,
      exchangeOptions,
      outOfStockError: itemOutOfStock,
      returnQuantity: itemQuantity > 1 ? '' : 1,
      exchangeSelectionRequired: !(allSelected && noEmptySelection) || itemOutOfStock,
      currentInventory,
      currentItem,
      selectedExchangeItem: updatedSelectedExchangeItem,
    });
  };

  /*
   * Re-compute selected(primary) dropdown exchange options based on inventory and
   * other selected attributes.
   * For qty dropdown, disable qty options if exchange_item_qty inventory stock is lesser
   */
  const getOnFocusOptions =
    (primaryAttr, primaryAttrValues = []) =>
    () => {
      const { inventory, selectedOptions, currentInventory, exchangeOptions } = sidebarState;
      const otherAttributes = Object.keys(selectedOptions).filter(
        (option) => option !== primaryAttr,
      );
      let newExchangeOptions = {};

      currentInventory.forEach((item) => {
        if (item.exchange_eligibility === false) {
          exchangeOptions[`${primaryAttr}SortOrder`] = exchangeOptions[
            `${primaryAttr}SortOrder`
          ].filter((key) => key !== item.attributes[primaryAttr]);
        }
      });

      if (primaryAttr !== 'quantity') {
        newExchangeOptions = primaryAttrValues.reduce(
          (newExgOptions, value) => {
            newExgOptions[primaryAttr][value] = inventory.some(
              (item) =>
                item.attributes[primaryAttr] === value &&
                otherAttributes.every((attribute) =>
                  selectedOptions[attribute]
                    ? item.attributes[attribute] === selectedOptions[attribute]
                    : true,
                ),
            );
            return newExgOptions;
          },
          { ...exchangeOptions },
        );

        updateState({
          exchangeOptions: newExchangeOptions,
        });
      } else {
        const { itemQuantity, productQtyOptions } = sidebarState;
        const unavailableQuantity = {};

        if (currentInventory[0].attributes.qty < itemQuantity) {
          for (let i = +currentInventory[0].attributes.qty + 1; i <= itemQuantity; i += 1) {
            unavailableQuantity[i] = false;
          }
        }

        updateState({
          itemQtyOptions: {
            ...productQtyOptions,
            ...unavailableQuantity,
          },
        });
      }
    };

  const checkReasonIsSelected = (currentReason, displayProperty) => {
    const { selectedReason, selectedNestedReason } = sidebarState;
    const reasonName = currentReason[displayProperty];
    const parentSelected = selectedReason && reasonName === selectedReason[displayProperty];
    const nestedSelected =
      selectedNestedReason && reasonName === selectedNestedReason[displayProperty];
    return parentSelected || nestedSelected;
  };

  const chooseReason = (index) => {
    const { exchangeReasons } = sidebarState;
    const selectedReason = exchangeReasons[index];
    const reasonJsonStr = selectedReason.return_reasons[0].return_reason_settings_json;
    const {
      commentHidden,
      commentRequired,
      commentPlaceholder,
      nestedReasonsTitle,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
    } = parseReasonJson(reasonJsonStr);
    const nestedReasons = getNestedReasons(selectedReason);
    store.dispatch(saveNestedDisplayedReturnReasons(nestedReasons));
    store.dispatch(saveSelectedReturnReason(selectedReason));

    return {
      selectedReason,
      commentHidden,
      commentRequired,
      commentPlaceholder,
      nestedReasonsTitle,
      nestedReasons,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
    };
  };

  const chooseNestedReason = (index) => {
    const selectedNestedReason = sidebarState.nestedReasons[index];
    const reasonJsonStr = selectedNestedReason.return_reason_settings_json;
    const {
      commentHidden,
      commentRequired,
      commentPlaceholder,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
    } = parseReasonJson(reasonJsonStr);
    store.dispatch(saveSelectedNestedReturnReason(selectedNestedReason));

    return {
      selectedNestedReason,
      commentHidden,
      commentRequired,
      commentPlaceholder,
      photoUploadEnabled,
      photoUploadRequired,
      photoUploadPlaceholder,
      photoUploadTitle,
      photoUploadMaxFiles,
    };
  };

  const handleSelectReason = (index) => {
    if (sidebarState.photoUploadEnabled) {
      store.dispatch(deleteCachedImages());
    }
    const newState = sidebarState.nestedReasons ? chooseNestedReason(index) : chooseReason(index);
    updateState(newState, false);
  };

  const resetVariantSelection = () => {
    const { options } = sidebarState;
    options.forEach(({ code }) => handleOptionChange(code)(null));
  };

  const resetReasons = () => {
    if (!unevenExchanges) {
      resetVariantSelection();
    }
    updateState({
      selectedReason: null,
      nestedReasons: null,
      selectedNestedReason: null,
    });
  };

  const renderSidebar = async () => {
    const handlers = {
      checkSelected,
      handleOptionChange,
      getOnFocusOptions,
      checkReasonIsSelected,
      handleSelectReason,
      resetReasons,
      handleUnevenExchangeSubmit,
    };
    const module = await import(
      /* webpackChunkName: "exchange-sidebar" */ `../../components/ExchangeSidebar/exchangeSidebar`
    );

    module.renderExchangeSidebar({
      sidebarState,
      actions,
      handlers,
      node,
    });
  };

  return {
    actions,
    setUpdateCallback,
  };
};

export const exchangeSidebar = exchangeSidebarComponent();
